import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import ProductPickerOrCreate from './ProductPickerOrCreate';
import { ExpiryDateInput } from './ExpiryDateInput';
import { PackageCheck, Loader2, Plus, Trash2, Truck, Sparkles } from 'lucide-react';
import { addOfflinePurchase, generateOfflineId } from '../lib/offlineDB';

const CURRENCY = (n) => (Number(n) || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'GHS', maximumFractionDigits: 2,
});

const emptyLine = () => ({
  _key: crypto.randomUUID(),
  picker: {},                // ProductPickerOrCreate output
  quantity: '',
  unit_cost: '',
  batch_number: '',
  expiry_date: '',
});

/**
 * DirectReceiptDialog — records goods that arrived without a
 * system-generated PO. Supports mixing existing SKUs and brand-new items in
 * the same receipt. Backend: `POST /api/purchases/direct-receipt`.
 */
export default function DirectReceiptDialog({ open, onOpenChange, onCreated }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [receiptDate, setReceiptDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/suppliers?limit=200').then(res => setSuppliers(res.data?.suppliers || res.data || [])).catch(() => {});
    // Reset when reopened
    setSupplierId(''); setInvoiceNumber(''); setNotes('');
    setReceiptDate(new Date().toISOString().slice(0, 10));
    setLines([emptyLine()]);
  }, [open]);

  const supplier = suppliers.find(s => s.supplier_id === supplierId);

  const updateLine = (idx, patch) => setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  // When the picker resolves, prefill unit_cost from the product if the buyer
  // hasn't already typed one.
  const onPickerChange = (idx, sel) => {
    updateLine(idx, {
      picker: sel || {},
      unit_cost: lines[idx].unit_cost || (sel?.unit_cost ? String(sel.unit_cost) : ''),
    });
  };

  const validLines = lines.filter(l => (l.picker?.product_id || l.picker?.new_product)
                                     && Number(l.quantity) > 0
                                     && Number(l.unit_cost) >= 0);
  const subtotal = validLines.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_cost), 0);
  const newItemCount = validLines.filter(l => !!l.picker.new_product).length;

  const submit = async () => {
    if (!supplierId || !supplier) return toast.error('Pick a supplier');
    if (validLines.length === 0) return toast.error('Add at least one item');
    if (validLines.some(l => l.expiry_date === null)) {
      return toast.error('Check the expiry dates', { description: 'Use 03/2027, MAR 2027 or 2027-03-31' });
    }

    const payload = {
      supplier_id: supplierId,
      supplier_name: supplier.name,
      invoice_number: invoiceNumber,
      receipt_date: receiptDate,
      notes,
      items: validLines.map(l => ({
        product_id: l.picker.product_id || '',
        product_name: l.picker.product_name || l.picker.new_product?.name || '',
        quantity: Number(l.quantity),
        unit_cost: Number(l.unit_cost),
        batch_number: l.batch_number || '',
        expiry_date: l.expiry_date || '',
        new_product: l.picker.new_product || null,
      })),
    };
    // Same id sent either way, so a lost response can't record the receipt twice.
    const offline_id = generateOfflineId();

    const saveOffline = async () => {
      await addOfflinePurchase({ ...payload, offline_id });
      toast.success('Receipt saved offline — it will be recorded once you reconnect');
      onOpenChange(false);
      onCreated?.();
    };

    setSubmitting(true);
    try {
      if (!navigator.onLine) return await saveOffline(); // skip the doomed request
      const res = await api.post('/purchases/direct-receipt', { ...payload, offline_id });
      const created = res.data?.auto_created_products?.length || 0;
      toast.success(`Recorded ${res.data.po_id} · ${validLines.length} line${validLines.length === 1 ? '' : 's'}${created ? ` · ${created} new product(s)` : ''}`);
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (err) {
      if (!err.response) return await saveOffline(); // network dropped mid-request
      toast.error(err?.response?.data?.detail || 'Direct receipt failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="direct-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-primary" /> Record Direct Receipt
          </DialogTitle>
          <DialogDescription>
            Log goods that arrived without a system-generated PO. Add existing SKUs or create new items inline — the receipt posts stock, drafts a matching PO in status <b>received</b>, and updates supplier A/P.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Supplier *</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="direct-receipt-supplier"><SelectValue placeholder="Pick supplier" /></SelectTrigger>
              <SelectContent>
                {suppliers.map(s => (
                  <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Receipt date</Label>
            <Input type="date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)} data-testid="direct-receipt-date" />
          </div>
          <div>
            <Label>Supplier invoice #</Label>
            <Input placeholder="Optional" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} data-testid="direct-receipt-invoice" />
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Items</Label>
            <Button type="button" size="sm" variant="outline" onClick={addLine} className="gap-1" data-testid="direct-receipt-add-line">
              <Plus className="h-3 w-3" /> Add line
            </Button>
          </div>
          {lines.map((line, idx) => (
            <div key={line._key} className="border rounded-lg p-3 space-y-2" data-testid={`direct-receipt-line-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_100px_120px_120px_140px_40px] gap-2 items-end">
                <div className="md:col-span-1">
                  <Label className="text-[11px]">Product</Label>
                  <ProductPickerOrCreate
                    value={line.picker}
                    onChange={(v) => onPickerChange(idx, v)}
                    testid={`direct-receipt-picker-${idx}`}
                    placeholder="Search or type new name…"
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Quantity</Label>
                  <Input type="number" min={0} value={line.quantity} onChange={e => updateLine(idx, { quantity: e.target.value })} data-testid={`direct-receipt-qty-${idx}`} />
                </div>
                <div>
                  <Label className="text-[11px]">Unit cost ₵</Label>
                  <Input type="number" step="0.01" min={0} value={line.unit_cost} onChange={e => updateLine(idx, { unit_cost: e.target.value })} data-testid={`direct-receipt-cost-${idx}`} />
                </div>
                <div>
                  <Label className="text-[11px]">Batch #</Label>
                  <Input value={line.batch_number} onChange={e => updateLine(idx, { batch_number: e.target.value })} placeholder="Optional" />
                </div>
                <div>
                  <Label className="text-[11px]">Expiry</Label>
                  <ExpiryDateInput
                    value={line.expiry_date}
                    onChange={(iso) => updateLine(idx, { expiry_date: iso })}
                    compact
                    testId={`direct-receipt-expiry-${idx}`}
                  />
                </div>
                <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(idx)} disabled={lines.length === 1} className="text-red-600" data-testid={`direct-receipt-remove-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {(line.picker?.product_id || line.picker?.new_product) && (
                <div className="flex items-center justify-end text-xs text-muted-foreground">
                  Line total: <span className="font-semibold ml-2 text-foreground">{CURRENCY(Number(line.quantity || 0) * Number(line.unit_cost || 0))}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div>
          <Label>Notes</Label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional receipt notes" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">{validLines.length} valid line{validLines.length === 1 ? '' : 's'}</span>
            {newItemCount > 0 && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 gap-1">
                <Sparkles className="h-3 w-3" /> {newItemCount} new item{newItemCount === 1 ? '' : 's'}
              </Badge>
            )}
            <span className="ml-2">Total: <b>{CURRENCY(subtotal)}</b></span>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button type="button" onClick={submit} disabled={submitting || validLines.length === 0 || !supplierId} className="gap-1.5" data-testid="direct-receipt-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
              Record receipt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
